/**
 * JsonExcelRenderer — Excel 高阶数据透视与异常高亮引擎
 *
 * 接收大模型通过 Zod Tool 输出的结构化 Excel JSON，
 * 使用 AG Grid 渲染带 textColor / backgroundColor 样式标注的交互式审查表格。
 * 支持多 Sheet Tab 切换。
 */

import React, { useState, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';

ModuleRegistry.registerModules([AllCommunityModule]);

// ── 类型定义 ──
export interface ExcelCell {
  value: string | number | boolean | null;
  bold?: boolean;
  italic?: boolean;
  textColor?: string;       // 'red' | '#FF0000' | ...
  backgroundColor?: string; // 'yellow' | '#FFFF00' | ...
  comment?: string;
}

export interface ExcelRow {
  cells: ExcelCell[];
}

export interface ExcelSheet {
  sheetName: string;
  headers?: string[];   // 列标题（可选，否则用 A, B, C...）
  rows: ExcelRow[];
}

export interface ExcelDocumentJson {
  fileName: string;
  sheets: ExcelSheet[];
}

// ── 颜色名到 CSS 值映射 ──
const COLOR_MAP: Record<string, string> = {
  red: '#ef4444', green: '#22c55e', blue: '#3b82f6',
  yellow: '#fbbf24', orange: '#f97316', gray: '#6b7280',
  white: '#ffffff', black: '#000000',
};

function resolveColor(c?: string): string | undefined {
  if (!c) return undefined;
  return COLOR_MAP[c.toLowerCase()] || c;
}

// ── 单个 Sheet 的 AG Grid 渲染 ──
const SheetGrid: React.FC<{ sheet: ExcelSheet }> = ({ sheet }) => {
  const { columnDefs, rowData } = useMemo(() => {
    if (sheet.rows.length === 0) return { columnDefs: [], rowData: [] };

    const colCount = Math.max(...sheet.rows.map(r => r.cells.length));

    // 构建列头
    const headers = sheet.headers || Array.from({ length: colCount }, (_, i) =>
      String.fromCharCode(65 + (i % 26)) + (i >= 26 ? String(Math.floor(i / 26)) : '')
    );

    const cols = headers.map((h, colIdx) => ({
      field: `col_${colIdx}`,
      headerName: h,
      sortable: true,
      resizable: true,
      minWidth: 100,
      flex: 1,
      cellStyle: (params: any) => {
        const meta = params.data?.[`_meta_${colIdx}`] as ExcelCell | undefined;
        if (!meta) return {};
        const style: Record<string, string> = {};
        if (meta.textColor) style.color = resolveColor(meta.textColor) || meta.textColor;
        if (meta.backgroundColor) style.backgroundColor = resolveColor(meta.backgroundColor) || meta.backgroundColor;
        if (meta.bold) style.fontWeight = '700';
        if (meta.italic) style.fontStyle = 'italic';
        return style;
      },
    }));

    // 构建行数据（同时保存元数据用于 cellStyle）
    const rows = sheet.rows.map((row) => {
      const rowObj: Record<string, any> = {};
      row.cells.forEach((cell, colIdx) => {
        rowObj[`col_${colIdx}`] = cell.value ?? '';
        rowObj[`_meta_${colIdx}`] = cell;
      });
      return rowObj;
    });

    return { columnDefs: cols, rowData: rows };
  }, [sheet]);

  if (rowData.length === 0) {
    return <div className="p-4 text-sm text-gray-400 italic">空 Sheet</div>;
  }

  return (
    <div style={{ height: Math.min(420, 42 + rowData.length * 36), width: '100%' }}>
      <AgGridReact
        columnDefs={columnDefs}
        rowData={rowData}
        domLayout="normal"
        suppressHorizontalScroll={false}
        defaultColDef={{ filter: true, minWidth: 80 }}
      />
    </div>
  );
};

// ── 主组件 ──
interface JsonExcelRendererProps {
  document: ExcelDocumentJson;
  onExport?: () => void;
}

const JsonExcelRenderer: React.FC<JsonExcelRendererProps> = ({ document, onExport }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = document.sheets[activeIdx];

  const totalRows = document.sheets.reduce((s, sh) => s + sh.rows.length, 0);

  return (
    <div className="my-4 rounded-lg overflow-hidden border border-gray-200 shadow-md bg-white">
      {/* 顶部标题栏 */}
      <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-emerald-50 to-white border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-lg">📊</span>
          <span className="text-sm font-semibold text-gray-800 truncate max-w-[240px]">{document.fileName}</span>
        </div>
        {onExport && (
          <button
            onClick={onExport}
            className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 transition-colors"
          >
            ⬇️ 导出 Excel
          </button>
        )}
      </div>

      {/* Sheet Tabs */}
      {document.sheets.length > 1 && (
        <div className="flex gap-0 border-b border-gray-200 bg-gray-50">
          {document.sheets.map((sh, i) => (
            <button
              key={i}
              onClick={() => setActiveIdx(i)}
              className={`px-4 py-1.5 text-xs font-medium border-r border-gray-200 transition-colors ${
                i === activeIdx
                  ? 'bg-white text-emerald-700 border-b-2 border-b-emerald-500'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {sh.sheetName}
            </button>
          ))}
        </div>
      )}

      {/* AG Grid */}
      {active && <SheetGrid sheet={active} />}

      {/* 底栏 */}
      <div className="flex items-center justify-between px-4 py-1.5 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
        <span>{totalRows} rows · {document.sheets.length} sheet(s)</span>
        <span className="text-gray-400">AI-Generated Spreadsheet</span>
      </div>
    </div>
  );
};

export default JsonExcelRenderer;
